import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { maintenanceAPI, vehicleAPI } from '../lib/api';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { 
    Plus, Search, Wrench, Edit, Trash2, Loader2, 
    MoreVertical, Filter, Calendar, AlertTriangle, Gauge, Bell, Truck, ChevronDown, ChevronUp,
    Fuel, Camera, Upload, Check, Car
} from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { SortableTh } from '../components/ui/sortable-header';

// Reminder type labels for display
const reminderTypeLabels = {
    tyre_change: { en: 'Tyre Change', el: 'Αλλαγή Ελαστικών' },
    oil_change: { en: 'Oil Change', el: 'Αλλαγή Λαδιών' },
    brake_service: { en: 'Brake Service', el: 'Σέρβις Φρένων' },
    filter_replacement: { en: 'Filter Replacement', el: 'Αλλαγή Φίλτρων' },
    battery_check: { en: 'Battery Check', el: 'Έλεγχος Μπαταρίας' },
    lease_return: { en: 'Lease Return', el: 'Επιστροφή Leasing' },
};

const initialFormData = {
    vehicle_id: '',
    maintenance_type: 'routine',
    description: '',
    schedule_by: 'date',
    scheduled_date: '',
    scheduled_mileage: '',
    service_interval_km: '',
    completed_date: '',
    cost: 0,
    status: 'scheduled',
    odometer_reading: '',
    technician: '',
    notes: ''
};

const maintenanceTypes = [
    'routine', 'oil_change', 'tire_rotation', 'brake_service', 
    'transmission', 'engine', 'electrical', 'body_work', 'inspection',
    'kteo', 'emissions_card', 'other'
];

// Labels for maintenance types (bilingual)
const maintenanceTypeLabels = {
    'routine': { en: 'Routine', el: 'Τακτική Συντήρηση' },
    'oil_change': { en: 'Oil Change', el: 'Αλλαγή Λαδιών' },
    'tire_rotation': { en: 'Tire Rotation', el: 'Εναλλαγή Ελαστικών' },
    'brake_service': { en: 'Brake Service', el: 'Σέρβις Φρένων' },
    'transmission': { en: 'Transmission', el: 'Κιβώτιο Ταχυτήτων' },
    'engine': { en: 'Engine', el: 'Κινητήρας' },
    'electrical': { en: 'Electrical', el: 'Ηλεκτρικά' },
    'body_work': { en: 'Body Work', el: 'Αμάξωμα' },
    'inspection': { en: 'Inspection', el: 'Επιθεώρηση' },
    'kteo': { en: 'KTEO (MOT)', el: 'ΚΤΕΟ' },
    'emissions_card': { en: 'Emissions Card', el: 'Κάρτα Καυσαερίων' },
    'other': { en: 'Other', el: 'Άλλο' }
};

const statusOptions = ['scheduled', 'in_progress', 'completed', 'overdue', 'cancelled'];

const fuelLevelOptions = [
    { value: 'empty', label: 'Empty', icon: '○○○○', color: 'text-red-500' },
    { value: 'quarter', label: '1/4', icon: '●○○○', color: 'text-orange-500' },
    { value: 'half', label: '1/2', icon: '●●○○', color: 'text-yellow-500' },
    { value: 'three_quarter', label: '3/4', icon: '●●●○', color: 'text-green-400' },
    { value: 'full', label: 'Full', icon: '●●●●', color: 'text-green-600' },
];

const photoTypes = ['front', 'back', 'left', 'right', 'interior_front', 'interior_back'];
const photoLabels = {
    front: 'Front',
    back: 'Back', 
    left: 'Left Side',
    right: 'Right Side',
    interior_front: 'Interior Front',
    interior_back: 'Interior Back'
};

const StatusBadge = ({ status }) => {
    const styles = {
        scheduled: 'badge-warning',
        in_progress: 'bg-blue-500/15 text-blue-400',
        completed: 'badge-success',
        overdue: 'badge-danger',
        cancelled: 'badge-default'
    };
    return (
        <span className={`badge ${styles[status] || 'badge-default'}`}>
            {status.replace('_', ' ')}
        </span>
    );
};

export default function MaintenancePage() {
    const { canManage, isAdmin } = useAuth();
    const { language } = useLanguage();
    const [records, setRecords] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [scheduledSort, setScheduledSort] = useState({ key: 'schedule', direction: 'asc' });
    const [completedSort, setCompletedSort] = useState({ key: 'date', direction: 'desc' });
    const [reminderSort, setReminderSort] = useState({ key: 'status', direction: 'asc' });

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [formData, setFormData] = useState(initialFormData);
    const [submitting, setSubmitting] = useState(false);
    const [showLeasingReminders, setShowLeasingReminders] = useState(true);
    const [showScheduledMaintenance, setShowScheduledMaintenance] = useState(true);
    const [showCompletedMaintenance, setShowCompletedMaintenance] = useState(true);
    
    // Replacement car state
    const [needsReplacementCar, setNeedsReplacementCar] = useState(false);
    const [replacementCarData, setReplacementCarData] = useState({
        make: '',
        model: '',
        license_plate: '',
        fuel_level: 'half',
        odometer_reading: '',
        notes: ''
    });
    const [replacementPhotos, setReplacementPhotos] = useState({});
    const [uploadingPhoto, setUploadingPhoto] = useState(null);
    const [createdMaintenanceId, setCreatedMaintenanceId] = useState(null);
    const [showReplacementModal, setShowReplacementModal] = useState(false);
    const [replacementStep, setReplacementStep] = useState(1); // 1: info, 2: photos
    const fileInputRefs = useRef({});

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [recordsRes, vehiclesRes] = await Promise.all([
                maintenanceAPI.getAll(),
                vehicleAPI.getAll()
            ]);
            setRecords(recordsRes.data);
            setVehicles(vehiclesRes.data);
        } catch (error) {
            toast.error('Failed to fetch data');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        try {
            const payload = {
                vehicle_id: formData.vehicle_id,
                maintenance_type: formData.maintenance_type,
                description: formData.description,
                scheduled_date: formData.scheduled_date || null,
                scheduled_mileage: formData.scheduled_mileage ? parseFloat(formData.scheduled_mileage) : null,
                service_interval_km: formData.service_interval_km ? parseFloat(formData.service_interval_km) : null,
                completed_date: formData.completed_date || null,
                cost: parseFloat(formData.cost) || 0,
                status: formData.status,
                odometer_reading: formData.odometer_reading ? parseFloat(formData.odometer_reading) : null,
                technician: formData.technician || null,
                notes: formData.notes || null
            };

            if (editingRecord) {
                await maintenanceAPI.update(editingRecord.id, payload);
                toast.success('Maintenance record updated');
                setIsDialogOpen(false);
                setEditingRecord(null);
                setFormData(initialFormData);
                fetchData();
            } else {
                const response = await maintenanceAPI.create(payload);
                toast.success('Maintenance record created');
                
                // If replacement car is needed, open the replacement modal
                if (needsReplacementCar) {
                    setCreatedMaintenanceId(response.data.id);
                    setIsDialogOpen(false);
                    setShowReplacementModal(true);
                    setReplacementStep(1);
                } else {
                    setIsDialogOpen(false);
                    setFormData(initialFormData);
                    fetchData();
                }
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Operation failed');
        } finally {
            setSubmitting(false);
        }
    };

    const handleReplacementCarSubmit = async () => {
        if (!replacementCarData.make || !replacementCarData.license_plate || !replacementCarData.odometer_reading) {
            toast.error('Please fill in all required fields (Make, License Plate, Kilometers)');
            return;
        }
        
        setSubmitting(true);
        try {
            const response = await api.post(`/maintenance/${createdMaintenanceId}/replacement-car`, {
                make: replacementCarData.make,
                model: replacementCarData.model || '',
                license_plate: replacementCarData.license_plate,
                fuel_level: replacementCarData.fuel_level,
                odometer_reading: parseFloat(replacementCarData.odometer_reading),
                notes: replacementCarData.notes
            });
            
            toast.success('Replacement car info saved');
            setReplacementStep(2); // Move to photo upload
            setCreatedMaintenanceId(response.data.id); // Now this becomes the replacement_id
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save replacement car');
        } finally {
            setSubmitting(false);
        }
    };

    const handleReplacementPhotoUpload = async (photoType, file) => {
        if (!file) return;
        
        setUploadingPhoto(photoType);
        const formData = new FormData();
        formData.append('photo_type', photoType);
        formData.append('file', file);
        
        try {
            await api.post(`/replacement-cars/${createdMaintenanceId}/photos`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            setReplacementPhotos(prev => ({
                ...prev,
                [photoType]: URL.createObjectURL(file)
            }));
            toast.success(`${photoLabels[photoType]} photo uploaded`);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Photo upload failed');
        } finally {
            setUploadingPhoto(null);
        }
    };

    const handleReplacementComplete = () => {
        setShowReplacementModal(false);
        setNeedsReplacementCar(false);
        setReplacementCarData({ make: '', model: '', license_plate: '', fuel_level: 'half', odometer_reading: '', notes: '' });
        setReplacementPhotos({});
        setCreatedMaintenanceId(null);
        setReplacementStep(1);
        setFormData(initialFormData);
        fetchData();
        toast.success('Replacement car setup complete!');
    };

    const handleEdit = (record) => {
        setEditingRecord(record);
        const hasDate = record.scheduled_date;
        const hasMileage = record.scheduled_mileage;
        let scheduleBy = 'date';
        if (hasMileage && hasDate) scheduleBy = 'both';
        else if (hasMileage) scheduleBy = 'distance';
        
        setFormData({
            vehicle_id: record.vehicle_id,
            maintenance_type: record.maintenance_type,
            description: record.description,
            schedule_by: scheduleBy,
            scheduled_date: record.scheduled_date || '',
            scheduled_mileage: record.scheduled_mileage || '',
            service_interval_km: record.service_interval_km || '',
            completed_date: record.completed_date || '',
            cost: record.cost,
            status: record.status,
            odometer_reading: record.odometer_reading || '',
            technician: record.technician || '',
            notes: record.notes || ''
        });
        setIsDialogOpen(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this record?')) return;
        
        try {
            await maintenanceAPI.delete(id);
            toast.success('Record deleted successfully');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Delete failed');
        }
    };

    const handleDialogClose = () => {
        setIsDialogOpen(false);
        setEditingRecord(null);
        setFormData(initialFormData);
    };

    const getVehicleInfo = (vehicleId) => {
        const vehicle = vehicles.find(v => v.id === vehicleId);
        return vehicle ? `${vehicle.make} ${vehicle.model} (${vehicle.license_plate})` : 'Unknown Vehicle';
    };

    const getVehicleMileage = (vehicleId) => {
        const vehicle = vehicles.find(v => v.id === vehicleId);
        return vehicle ? vehicle.current_mileage : 0;
    };

    const getMileageStatus = (record) => {
        if (!record.scheduled_mileage) return null;
        const vehicle = vehicles.find(v => v.id === record.vehicle_id);
        if (!vehicle) return null;
        
        const currentMileage = vehicle.current_mileage;
        const scheduledMileage = record.scheduled_mileage;
        const remaining = scheduledMileage - currentMileage;
        
        if (remaining <= 0) return { status: 'overdue', remaining: Math.abs(remaining) };
        if (remaining <= 500) return { status: 'soon', remaining };
        return { status: 'ok', remaining };
    };

    const toggleSort = (setter) => (key) => setter(prev => (
        prev.key === key
            ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
            : { key, direction: 'asc' }
    ));

    const recordSortValue = (record, key) => {
        const vehicle = vehicles.find(v => v.id === record.vehicle_id);
        switch (key) {
            case 'vehicle': return `${vehicle?.license_plate || ''} ${vehicle?.make || ''}`.toLowerCase();
            case 'type': return (record.maintenance_type || '').toLowerCase();
            case 'description': return (record.description || '').toLowerCase();
            case 'schedule': return record.scheduled_date
                ? new Date(record.scheduled_date).getTime()
                : (record.scheduled_mileage || 0);
            case 'date': return new Date(record.completed_date || record.scheduled_date || 0).getTime();
            case 'cost': return record.cost ?? 0;
            case 'status': return (record.status || '').toLowerCase();
            default: return '';
        }
    };

    const sortRecords = (list, config) => {
        if (!config.key) return list;
        const factor = config.direction === 'asc' ? 1 : -1;
        return [...list].sort((a, b) => {
            const va = recordSortValue(a, config.key);
            const vb = recordSortValue(b, config.key);
            if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * factor;
            return String(va).localeCompare(String(vb), 'el') * factor;
        });
    };

    const reminderSortValue = (item, key) => {
        switch (key) {
            case 'vehicle': return `${item.vehicle.license_plate || ''} ${item.vehicle.make || ''}`.toLowerCase();
            case 'reminderType': return (item.reminder.type || '').toLowerCase();
            case 'target': return item.reminder.km || new Date(item.reminder.date || 0).getTime();
            case 'status': return { overdue: 0, soon: 1, ok: 2 }[item.status] ?? 3;
            case 'remaining': return item.remaining ?? item.daysRemaining ?? 0;
            default: return '';
        }
    };

    const sortReminders = (list, config) => {
        if (!config.key) return list;
        const factor = config.direction === 'asc' ? 1 : -1;
        return [...list].sort((a, b) => {
            const va = reminderSortValue(a, config.key);
            const vb = reminderSortValue(b, config.key);
            if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * factor;
            return String(va).localeCompare(String(vb), 'el') * factor;
        });
    };

    const filteredRecords = records.filter(record => {
        const vehicle = vehicles.find(v => v.id === record.vehicle_id);
        const matchesSearch = 
            record.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
            record.maintenance_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (vehicle && vehicle.license_plate.toLowerCase().includes(searchTerm.toLowerCase()));
        const matchesStatus = filterStatus === 'all' || record.status === filterStatus;
        return matchesSearch && matchesStatus;
    });

    // Separate scheduled and completed maintenance
    const scheduledRecords = sortRecords(
        filteredRecords.filter(record => ['scheduled', 'in_progress', 'overdue'].includes(record.status)),
        scheduledSort
    );
    const completedRecords = sortRecords(
        filteredRecords.filter(record => ['completed', 'cancelled'].includes(record.status)),
        completedSort
    );

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(value);
    };

    // Get all leasing vehicles with their reminder status
    const getLeasingReminders = () => {
        const reminders = [];
        
        vehicles.forEach(vehicle => {
            if (vehicle.ownership === 'leasing' && vehicle.leasing_reminders && vehicle.leasing_reminders.length > 0) {
                vehicle.leasing_reminders.forEach(reminder => {
                    let status = 'ok';
                    let remaining = null;
                    let daysRemaining = null;
                    
                    if (reminder.km) {
                        remaining = reminder.km - vehicle.current_mileage;
                        if (remaining <= 0) {
                            status = 'overdue';
                        } else if (remaining <= 2000) {
                            status = 'soon';
                        }
                    }
                    
                    if (reminder.date) {
                        const reminderDate = new Date(reminder.date);
                        const today = new Date();
                        daysRemaining = Math.ceil((reminderDate - today) / (1000 * 60 * 60 * 24));
                        
                        if (daysRemaining <= 0) {
                            status = 'overdue';
                        } else if (daysRemaining <= 30) {
                            status = 'soon';
                        }
                    }
                    
                    reminders.push({
                        vehicle,
                        reminder,
                        status,
                        remaining,
                        daysRemaining
                    });
                });
            }
        });
        
        // Sort by status: overdue first, then soon, then ok
        return reminders.sort((a, b) => {
            const order = { overdue: 0, soon: 1, ok: 2 };
            return order[a.status] - order[b.status];
        });
    };

    const leasingReminders = sortReminders(getLeasingReminders(), reminderSort);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <Loader2 className="w-8 h-8 text-[#1B7EB5] animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="maintenance-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">{language === 'el' ? 'Συντήρηση' : 'Maintenance'}</h1>
                    <p className="text-slate-500">{records.length} {language === 'el' ? 'εγγραφές συνολικά' : 'total records'}</p>
                </div>
                
                {canManage() && (
                    <Dialog open={isDialogOpen} onOpenChange={(open) => open ? setIsDialogOpen(true) : handleDialogClose()}>
                        <DialogTrigger asChild>
                            <Button 
                                className="bg-[#1B7EB5] hover:bg-[#58A6B4] text-white rounded-sm"
                                data-testid="add-maintenance-btn"
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                {language === 'el' ? 'Προγραμματισμός Συντήρησης' : 'Schedule Maintenance'}
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-white border-slate-200 max-w-2xl max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                                <DialogTitle className="text-slate-800">
                                    {editingRecord ? 'Edit Maintenance Record' : 'Schedule New Maintenance'}
                                </DialogTitle>
                            </DialogHeader>
                            <form onSubmit={handleSubmit} className="space-y-4 mt-4 pr-2">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="form-group">
                                        <Label className="form-label">Vehicle</Label>
                                        <Select 
                                            value={formData.vehicle_id} 
                                            onValueChange={(value) => setFormData({...formData, vehicle_id: value})}
                                        >
                                            <SelectTrigger className="bg-white border-slate-300 text-slate-900" data-testid="maintenance-vehicle-select">
                                                <SelectValue placeholder="Select vehicle" />
                                            </SelectTrigger>
                                            <SelectContent className="bg-white border-slate-200">
                                                {vehicles.map(vehicle => (
                                                    <SelectItem key={vehicle.id} value={vehicle.id}>
                                                        {vehicle.make} {vehicle.model} ({vehicle.license_plate}) - {vehicle.current_mileage.toLocaleString()} km
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="form-group">
                                        <Label className="form-label">{language === 'el' ? 'Τύπος Συντήρησης' : 'Maintenance Type'}</Label>
                                        <Select 
                                            value={formData.maintenance_type} 
                                            onValueChange={(value) => setFormData({...formData, maintenance_type: value})}
                                        >
                                            <SelectTrigger className="bg-white border-slate-300 text-slate-900" data-testid="maintenance-type-select">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="bg-white border-slate-200">
                                                {maintenanceTypes.map(type => (
                                                    <SelectItem key={type} value={type}>
                                                        {maintenanceTypeLabels[type]?.[language] || type.replace('_', ' ')}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <Label className="form-label">Description</Label>
                                    <Textarea
                                        value={formData.description}
                                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                                        className="bg-white border-slate-300 text-slate-900 min-h-[80px]"
                                        placeholder="Describe the maintenance work needed..."
                                        required
                                        data-testid="maintenance-description-input"
                                    />
                                </div>

                                {/* Schedule By Selection */}
                                <div className="form-group">
                                    <Label className="form-label">Schedule Service By</Label>
                                    <Select 
                                        value={formData.schedule_by} 
                                        onValueChange={(value) => setFormData({...formData, schedule_by: value})}
                                    >
                                        <SelectTrigger className="bg-white border-slate-300 text-slate-900" data-testid="maintenance-schedule-by-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-white border-slate-200">
                                            <SelectItem value="date">
                                                <span className="flex items-center gap-2"><Calendar className="w-4 h-4" /> By Date</span>
                                            </SelectItem>
                                            <SelectItem value="distance">
                                                <span className="flex items-center gap-2"><Gauge className="w-4 h-4" /> By Distance (Mileage)</span>
                                            </SelectItem>
                                            <SelectItem value="both">
                                                <span className="flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Both (Whichever comes first)</span>
                                            </SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* Conditional Date/Distance Fields */}
                                <div className="grid grid-cols-2 gap-4">
                                    {(formData.schedule_by === 'date' || formData.schedule_by === 'both') && (
                                        <div className="form-group">
                                            <Label className="form-label flex items-center gap-2">
                                                <Calendar className="w-4 h-4 text-[#1B7EB5]" />
                                                Scheduled Date
                                            </Label>
                                            <Input
                                                type="date"
                                                value={formData.scheduled_date}
                                                onChange={(e) => setFormData({...formData, scheduled_date: e.target.value})}
                                                className="bg-white border-slate-300 text-slate-900"
                                                required={formData.schedule_by === 'date'}
                                                data-testid="maintenance-scheduled-input"
                                            />
                                        </div>
                                    )}
                                    {(formData.schedule_by === 'distance' || formData.schedule_by === 'both') && (
                                        <div className="form-group">
                                            <Label className="form-label flex items-center gap-2">
                                                <Gauge className="w-4 h-4 text-green-500" />
                                                Service at Mileage (km)
                                            </Label>
                                            <Input
                                                type="number"
                                                value={formData.scheduled_mileage}
                                                onChange={(e) => setFormData({...formData, scheduled_mileage: e.target.value})}
                                                className="bg-white border-slate-300 text-slate-900"
                                                placeholder="e.g., 20000"
                                                min="0"
                                                required={formData.schedule_by === 'distance'}
                                                data-testid="maintenance-mileage-input"
                                            />
                                        </div>
                                    )}
                                    {(formData.schedule_by === 'distance' || formData.schedule_by === 'both') && (
                                        <div className="form-group">
                                            <Label className="form-label">Repeat Every (km) - Optional</Label>
                                            <Input
                                                type="number"
                                                value={formData.service_interval_km}
                                                onChange={(e) => setFormData({...formData, service_interval_km: e.target.value})}
                                                className="bg-white border-slate-300 text-slate-900"
                                                placeholder="e.g., 5000 for oil change"
                                                min="0"
                                                data-testid="maintenance-interval-input"
                                            />
                                            <p className="text-xs text-zinc-500 mt-1">For recurring services like oil changes</p>
                                        </div>
                                    )}
                                    <div className="form-group">
                                        <Label className="form-label">Completed Date</Label>
                                        <Input
                                            type="date"
                                            value={formData.completed_date}
                                            onChange={(e) => setFormData({...formData, completed_date: e.target.value})}
                                            className="bg-white border-slate-300 text-slate-900"
                                            data-testid="maintenance-completed-input"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-3 gap-4">
                                    <div className="form-group">
                                        <Label className="form-label">Cost ($)</Label>
                                        <Input
                                            type="number"
                                            value={formData.cost}
                                            onChange={(e) => setFormData({...formData, cost: e.target.value})}
                                            className="bg-white border-slate-300 text-slate-900"
                                            min="0"
                                            step="0.01"
                                            data-testid="maintenance-cost-input"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <Label className="form-label">Status</Label>
                                        <Select 
                                            value={formData.status} 
                                            onValueChange={(value) => setFormData({...formData, status: value})}
                                        >
                                            <SelectTrigger className="bg-white border-slate-300 text-slate-900" data-testid="maintenance-status-select">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="bg-white border-slate-200">
                                                {statusOptions.map(status => (
                                                    <SelectItem key={status} value={status} className="capitalize">
                                                        {status.replace('_', ' ')}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="form-group">
                                        <Label className="form-label">Odometer (km)</Label>
                                        <Input
                                            type="number"
                                            value={formData.odometer_reading}
                                            onChange={(e) => setFormData({...formData, odometer_reading: e.target.value})}
                                            className="bg-white border-slate-300 text-slate-900"
                                            min="0"
                                            data-testid="maintenance-odometer-input"
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <Label className="form-label">Technician</Label>
                                    <Input
                                        value={formData.technician}
                                        onChange={(e) => setFormData({...formData, technician: e.target.value})}
                                        className="bg-white border-slate-300 text-slate-900"
                                        placeholder="Technician name"
                                        data-testid="maintenance-technician-input"
                                    />
                                </div>

                                <div className="form-group">
                                    <Label className="form-label">Notes</Label>
                                    <Textarea
                                        value={formData.notes}
                                        onChange={(e) => setFormData({...formData, notes: e.target.value})}
                                        className="bg-white border-slate-300 text-slate-900"
                                        placeholder="Additional notes..."
                                        data-testid="maintenance-notes-input"
                                    />
                                </div>

                                {/* Replacement Car Option - Only for new records */}
                                {!editingRecord && formData.status === 'scheduled' && (
                                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                                        <label className="flex items-center gap-3 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={needsReplacementCar}
                                                onChange={(e) => setNeedsReplacementCar(e.target.checked)}
                                                className="w-5 h-5 rounded border-blue-300 text-blue-600 focus:ring-blue-500"
                                                data-testid="needs-replacement-car-checkbox"
                                            />
                                            <div className="flex items-center gap-2">
                                                <Car className="w-5 h-5 text-blue-600" />
                                                <span className="font-medium text-blue-800">Need a replacement car?</span>
                                            </div>
                                        </label>
                                        {needsReplacementCar && (
                                            <p className="text-sm text-blue-600 mt-2 ml-8">
                                                You'll be prompted to record replacement car details after creating this maintenance record.
                                            </p>
                                        )}
                                    </div>
                                )}

                                <div className="flex justify-end gap-3 pt-4">
                                    <Button 
                                        type="button" 
                                        variant="outline" 
                                        onClick={handleDialogClose}
                                        className="border-zinc-700 text-slate-500 hover:text-slate-700"
                                    >
                                        Cancel
                                    </Button>
                                    <Button 
                                        type="submit" 
                                        className="bg-[#1B7EB5] hover:bg-[#58A6B4] text-white"
                                        disabled={submitting}
                                        data-testid="maintenance-submit-btn"
                                    >
                                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingRecord ? 'Update' : 'Create')}
                                    </Button>
                                </div>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative sm:w-1/2">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                        placeholder={language === 'el' ? 'Αναζήτηση με περιγραφή, τύπο ή όχημα...' : 'Search by description, type, or vehicle...'}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 bg-white border-slate-300 text-slate-900"
                        data-testid="maintenance-search-input"
                    />
                </div>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="sm:w-1/2 bg-white border-slate-300 text-slate-900" data-testid="maintenance-filter-status">
                        <Filter className="w-4 h-4 mr-2" />
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                        <SelectItem value="all">{language === 'el' ? 'Όλες οι καταστάσεις' : 'All Status'}</SelectItem>
                        {statusOptions.map(status => (
                            <SelectItem key={status} value={status} className="capitalize">
                                {status.replace('_', ' ')}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Leasing Reminders Section */}
            {leasingReminders.length > 0 && (
                <div className="fleet-card" data-testid="leasing-reminders-section">
                    <button 
                        onClick={() => setShowLeasingReminders(!showLeasingReminders)}
                        className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                    >
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                                <Bell className="w-5 h-5 text-purple-600" />
                            </div>
                            <div className="text-left">
                                <h3 className="font-semibold text-slate-800">
                                    {language === 'el' ? 'Υπενθυμίσεις Leasing' : 'Leasing Reminders'}
                                </h3>
                                <p className="text-sm text-slate-500">
                                    {leasingReminders.filter(r => r.status === 'overdue').length > 0 && (
                                        <span className="text-red-600 font-medium mr-2">
                                            {leasingReminders.filter(r => r.status === 'overdue').length} {language === 'el' ? 'εκπρόθεσμα' : 'overdue'}
                                        </span>
                                    )}
                                    {leasingReminders.filter(r => r.status === 'soon').length > 0 && (
                                        <span className="text-yellow-600 font-medium mr-2">
                                            {leasingReminders.filter(r => r.status === 'soon').length} {language === 'el' ? 'σύντομα' : 'upcoming'}
                                        </span>
                                    )}
                                    {leasingReminders.length} {language === 'el' ? 'συνολικά' : 'total'}
                                </p>
                            </div>
                        </div>
                        {showLeasingReminders ? (
                            <ChevronUp className="w-5 h-5 text-slate-400" />
                        ) : (
                            <ChevronDown className="w-5 h-5 text-slate-400" />
                        )}
                    </button>
                    
                    {showLeasingReminders && (
                        <div className="border-t border-slate-200">
                            <div className="overflow-x-auto">
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            <SortableTh label={language === 'el' ? 'Όχημα' : 'Vehicle'} sortKey="vehicle" sortConfig={reminderSort} onSort={toggleSort(setReminderSort)} />
                                            <SortableTh label={language === 'el' ? 'Τύπος Υπενθύμισης' : 'Reminder Type'} sortKey="reminderType" sortConfig={reminderSort} onSort={toggleSort(setReminderSort)} />
                                            <SortableTh label={language === 'el' ? 'Στόχος' : 'Target'} sortKey="target" sortConfig={reminderSort} onSort={toggleSort(setReminderSort)} />
                                            <SortableTh label={language === 'el' ? 'Κατάσταση' : 'Status'} sortKey="status" sortConfig={reminderSort} onSort={toggleSort(setReminderSort)} />
                                            <SortableTh label={language === 'el' ? 'Υπόλοιπο' : 'Remaining'} sortKey="remaining" sortConfig={reminderSort} onSort={toggleSort(setReminderSort)} />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {leasingReminders.map((item, idx) => (
                                            <tr key={`${item.vehicle.id}-${item.reminder.type}-${idx}`} className="table-row-hover">
                                                <td>
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-8 h-8 bg-purple-100 rounded flex items-center justify-center">
                                                            <Truck className="w-4 h-4 text-purple-600" />
                                                        </div>
                                                        <div>
                                                            <p className="font-medium text-slate-800">
                                                                {item.vehicle.make} {item.vehicle.model}
                                                            </p>
                                                            <p className="text-xs text-slate-500">{item.vehicle.license_plate}</p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className="font-medium text-slate-700">
                                                        {reminderTypeLabels[item.reminder.type]?.[language] || item.reminder.type}
                                                    </span>
                                                </td>
                                                <td>
                                                    {item.reminder.km && (
                                                        <span className="text-slate-600">
                                                            {item.reminder.km.toLocaleString()} km
                                                        </span>
                                                    )}
                                                    {item.reminder.date && (
                                                        <span className="text-slate-600">
                                                            {new Date(item.reminder.date).toLocaleDateString(language === 'el' ? 'el-GR' : 'en-US')}
                                                        </span>
                                                    )}
                                                </td>
                                                <td>
                                                    <span className={`badge ${
                                                        item.status === 'overdue' ? 'badge-danger' :
                                                        item.status === 'soon' ? 'badge-warning' :
                                                        'badge-success'
                                                    }`}>
                                                        {item.status === 'overdue' 
                                                            ? (language === 'el' ? 'Εκπρόθεσμο' : 'Overdue')
                                                            : item.status === 'soon'
                                                            ? (language === 'el' ? 'Σύντομα' : 'Soon')
                                                            : (language === 'el' ? 'Εντάξει' : 'OK')
                                                        }
                                                    </span>
                                                </td>
                                                <td>
                                                    {item.remaining !== null && (
                                                        <span className={`font-medium ${
                                                            item.status === 'overdue' ? 'text-red-600' :
                                                            item.status === 'soon' ? 'text-yellow-600' :
                                                            'text-green-600'
                                                        }`}>
                                                            {item.status === 'overdue' 
                                                                ? `${Math.abs(item.remaining).toLocaleString()} km ${language === 'el' ? 'πάνω' : 'over'}`
                                                                : `${item.remaining.toLocaleString()} km ${language === 'el' ? 'απομένουν' : 'left'}`
                                                            }
                                                        </span>
                                                    )}
                                                    {item.daysRemaining !== null && (
                                                        <span className={`font-medium ${
                                                            item.status === 'overdue' ? 'text-red-600' :
                                                            item.status === 'soon' ? 'text-yellow-600' :
                                                            'text-green-600'
                                                        }`}>
                                                            {item.status === 'overdue' 
                                                                ? `${Math.abs(item.daysRemaining)} ${language === 'el' ? 'μέρες πέρασαν' : 'days ago'}`
                                                                : `${item.daysRemaining} ${language === 'el' ? 'μέρες' : 'days'}`
                                                            }
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Scheduled Maintenance Section */}
            <div className="fleet-card" data-testid="scheduled-maintenance-section">
                <button 
                    onClick={() => setShowScheduledMaintenance(!showScheduledMaintenance)}
                    className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                >
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                            <Calendar className="w-5 h-5 text-yellow-600" />
                        </div>
                        <div className="text-left">
                            <h3 className="font-semibold text-slate-800">
                                {language === 'el' ? 'Προγραμματισμένη Συντήρηση' : 'Scheduled Maintenance'}
                            </h3>
                            <p className="text-sm text-slate-500">
                                {scheduledRecords.filter(r => r.status === 'overdue').length > 0 && (
                                    <span className="text-red-600 font-medium mr-2">
                                        {scheduledRecords.filter(r => r.status === 'overdue').length} {language === 'el' ? 'εκπρόθεσμα' : 'overdue'}
                                    </span>
                                )}
                                {scheduledRecords.filter(r => r.status === 'in_progress').length > 0 && (
                                    <span className="text-blue-600 font-medium mr-2">
                                        {scheduledRecords.filter(r => r.status === 'in_progress').length} {language === 'el' ? 'σε εξέλιξη' : 'in progress'}
                                    </span>
                                )}
                                {scheduledRecords.length} {language === 'el' ? 'συνολικά' : 'total'}
                            </p>
                        </div>
                    </div>
                    {showScheduledMaintenance ? (
                        <ChevronUp className="w-5 h-5 text-slate-400" />
                    ) : (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                    )}
                </button>
                
                {showScheduledMaintenance && (
                    <div className="border-t border-slate-200">
                        <div className="overflow-x-auto">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <SortableTh label={language === 'el' ? 'Όχημα' : 'Vehicle'} sortKey="vehicle" sortConfig={scheduledSort} onSort={toggleSort(setScheduledSort)} />
                                        <SortableTh label={language === 'el' ? 'Τύπος' : 'Type'} sortKey="type" sortConfig={scheduledSort} onSort={toggleSort(setScheduledSort)} />
                                        <SortableTh label={language === 'el' ? 'Περιγραφή' : 'Description'} sortKey="description" sortConfig={scheduledSort} onSort={toggleSort(setScheduledSort)} />
                                        <SortableTh label={language === 'el' ? 'Πρόγραμμα' : 'Schedule'} sortKey="schedule" sortConfig={scheduledSort} onSort={toggleSort(setScheduledSort)} />
                                        <SortableTh label={language === 'el' ? 'Κόστος' : 'Cost'} sortKey="cost" sortConfig={scheduledSort} onSort={toggleSort(setScheduledSort)} />
                                        <SortableTh label={language === 'el' ? 'Κατάσταση' : 'Status'} sortKey="status" sortConfig={scheduledSort} onSort={toggleSort(setScheduledSort)} />
                                        {canManage() && <th className="w-12">{language === 'el' ? 'Ενέργειες' : 'Actions'}</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {scheduledRecords.length === 0 ? (
                                        <tr>
                                            <td colSpan={canManage() ? 7 : 6} className="text-center py-8">
                                                <div className="empty-state">
                                                    <Calendar className="empty-state-icon mx-auto" />
                                                    <p>{language === 'el' ? 'Δεν υπάρχει προγραμματισμένη συντήρηση' : 'No scheduled maintenance'}</p>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        scheduledRecords.map((record) => {
                                            const mileageStatus = getMileageStatus(record);
                                            return (
                                            <tr key={record.id} className="table-row-hover" data-testid={`maintenance-row-${record.id}`}>
                                                <td>
                                                    <div className="flex items-center gap-2">
                                                        <Wrench className="w-4 h-4 text-slate-500" />
                                                        <span className="text-slate-800 font-medium">{getVehicleInfo(record.vehicle_id)}</span>
                                                    </div>
                                                </td>
                                                <td className="capitalize">
                                                    <div className="flex items-center gap-2">
                                                        <span>{maintenanceTypeLabels[record.maintenance_type]?.[language] || record.maintenance_type.replace('_', ' ')}</span>
                                                        {record.auto_generated && (
                                                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                                                                Auto
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="max-w-xs truncate">{record.description}</td>
                                                <td>
                                                    <div className="space-y-1">
                                                        {record.scheduled_date && (
                                                            <div className="flex items-center gap-2">
                                                                <Calendar className="w-4 h-4 text-[#1B7EB5]" />
                                                                <span>{new Date(record.scheduled_date).toLocaleDateString(language === 'el' ? 'el-GR' : 'en-US')}</span>
                                                            </div>
                                                        )}
                                                        {record.scheduled_mileage && (
                                                            <div className="flex items-center gap-2">
                                                                <Gauge className={`w-4 h-4 ${mileageStatus?.status === 'overdue' ? 'text-red-500' : mileageStatus?.status === 'soon' ? 'text-[#1B7EB5]' : 'text-green-500'}`} />
                                                                <span className={mileageStatus?.status === 'overdue' ? 'text-red-400' : mileageStatus?.status === 'soon' ? 'text-[#58A6B4]' : ''}>
                                                                    {record.scheduled_mileage.toLocaleString()} km
                                                                </span>
                                                                {mileageStatus && (
                                                                    <span className={`text-xs ${mileageStatus.status === 'overdue' ? 'text-red-400' : mileageStatus.status === 'soon' ? 'text-[#58A6B4]' : 'text-zinc-500'}`}>
                                                                        ({mileageStatus.status === 'overdue' ? `${mileageStatus.remaining.toLocaleString()} km ${language === 'el' ? 'πάνω' : 'overdue'}` : `${mileageStatus.remaining.toLocaleString()} km ${language === 'el' ? 'απομένουν' : 'left'}`})
                                                                    </span>
                                                                )}
                                                            </div>
                                                        )}
                                                        {record.service_interval_km && (
                                                            <div className="text-xs text-zinc-500">
                                                                {language === 'el' ? 'Επαναλαμβάνεται κάθε' : 'Repeats every'} {record.service_interval_km.toLocaleString()} km
                                                            </div>
                                                        )}
                                                        {!record.scheduled_date && !record.scheduled_mileage && (
                                                            <span className="text-zinc-500">{language === 'el' ? 'Μη προγραμματισμένο' : 'Not scheduled'}</span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="text-[#1B7EB5] font-medium">{formatCurrency(record.cost)}</td>
                                                <td><StatusBadge status={record.status} /></td>
                                                {canManage() && (
                                                    <td>
                                                        <DropdownMenu>
                                                            <DropdownMenuTrigger asChild>
                                                                <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-500 hover:text-slate-700">
                                                                    <MoreVertical className="w-4 h-4" />
                                                                </Button>
                                                            </DropdownMenuTrigger>
                                                            <DropdownMenuContent align="end" className="bg-white border-slate-200">
                                                                <DropdownMenuItem 
                                                                    onClick={() => handleEdit(record)}
                                                                    className="cursor-pointer"
                                                                >
                                                                    <Edit className="w-4 h-4 mr-2" />
                                                                    {language === 'el' ? 'Επεξεργασία' : 'Edit'}
                                                                </DropdownMenuItem>
                                                                {isAdmin() && (
                                                                    <DropdownMenuItem 
                                                                        onClick={() => handleDelete(record.id)}
                                                                        className="cursor-pointer text-red-400 focus:text-red-400"
                                                                    >
                                                                        <Trash2 className="w-4 h-4 mr-2" />
                                                                        {language === 'el' ? 'Διαγραφή' : 'Delete'}
                                                                    </DropdownMenuItem>
                                                                )}
                                                            </DropdownMenuContent>
                                                        </DropdownMenu>
                                                    </td>
                                                )}
                                            </tr>
                                        )})
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            {/* Completed Maintenance Section */}
            <div className="fleet-card" data-testid="completed-maintenance-section">
                <button 
                    onClick={() => setShowCompletedMaintenance(!showCompletedMaintenance)}
                    className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                >
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                            <Wrench className="w-5 h-5 text-green-600" />
                        </div>
                        <div className="text-left">
                            <h3 className="font-semibold text-slate-800">
                                {language === 'el' ? 'Ολοκληρωμένη Συντήρηση' : 'Completed Maintenance'}
                            </h3>
                            <p className="text-sm text-slate-500">
                                {completedRecords.filter(r => r.status === 'completed').length} {language === 'el' ? 'ολοκληρώθηκαν' : 'completed'}
                                {completedRecords.filter(r => r.status === 'cancelled').length > 0 && (
                                    <span className="text-slate-400 ml-2">
                                        • {completedRecords.filter(r => r.status === 'cancelled').length} {language === 'el' ? 'ακυρώθηκαν' : 'cancelled'}
                                    </span>
                                )}
                            </p>
                        </div>
                    </div>
                    {showCompletedMaintenance ? (
                        <ChevronUp className="w-5 h-5 text-slate-400" />
                    ) : (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                    )}
                </button>
                
                {showCompletedMaintenance && (
                    <div className="border-t border-slate-200">
                        <div className="overflow-x-auto">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <SortableTh label={language === 'el' ? 'Όχημα' : 'Vehicle'} sortKey="vehicle" sortConfig={completedSort} onSort={toggleSort(setCompletedSort)} />
                                        <SortableTh label={language === 'el' ? 'Τύπος' : 'Type'} sortKey="type" sortConfig={completedSort} onSort={toggleSort(setCompletedSort)} />
                                        <SortableTh label={language === 'el' ? 'Περιγραφή' : 'Description'} sortKey="description" sortConfig={completedSort} onSort={toggleSort(setCompletedSort)} />
                                        <SortableTh label={language === 'el' ? 'Ημερομηνία' : 'Date'} sortKey="date" sortConfig={completedSort} onSort={toggleSort(setCompletedSort)} />
                                        <SortableTh label={language === 'el' ? 'Κόστος' : 'Cost'} sortKey="cost" sortConfig={completedSort} onSort={toggleSort(setCompletedSort)} />
                                        <SortableTh label={language === 'el' ? 'Κατάσταση' : 'Status'} sortKey="status" sortConfig={completedSort} onSort={toggleSort(setCompletedSort)} />
                                        {canManage() && <th className="w-12">{language === 'el' ? 'Ενέργειες' : 'Actions'}</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {completedRecords.length === 0 ? (
                                        <tr>
                                            <td colSpan={canManage() ? 7 : 6} className="text-center py-8">
                                                <div className="empty-state">
                                                    <Wrench className="empty-state-icon mx-auto" />
                                                    <p>{language === 'el' ? 'Δεν υπάρχει ολοκληρωμένη συντήρηση' : 'No completed maintenance'}</p>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        completedRecords.map((record) => (
                                            <tr key={record.id} className="table-row-hover" data-testid={`maintenance-row-${record.id}`}>
                                                <td>
                                                    <div className="flex items-center gap-2">
                                                        <Wrench className="w-4 h-4 text-slate-500" />
                                                        <span className="text-slate-800 font-medium">{getVehicleInfo(record.vehicle_id)}</span>
                                                    </div>
                                                </td>
                                                <td className="capitalize">
                                                    <div className="flex items-center gap-2">
                                                        <span>{maintenanceTypeLabels[record.maintenance_type]?.[language] || record.maintenance_type.replace('_', ' ')}</span>
                                                        {record.auto_generated && (
                                                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                                                                Auto
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="max-w-xs truncate">{record.description}</td>
                                                <td>
                                                    {record.completed_date ? (
                                                        <div className="flex items-center gap-2">
                                                            <Calendar className="w-4 h-4 text-green-500" />
                                                            <span>{new Date(record.completed_date).toLocaleDateString(language === 'el' ? 'el-GR' : 'en-US')}</span>
                                                        </div>
                                                    ) : record.scheduled_date ? (
                                                        <div className="flex items-center gap-2">
                                                            <Calendar className="w-4 h-4 text-slate-400" />
                                                            <span>{new Date(record.scheduled_date).toLocaleDateString(language === 'el' ? 'el-GR' : 'en-US')}</span>
                                                        </div>
                                                    ) : (
                                                        <span className="text-zinc-500">-</span>
                                                    )}
                                                </td>
                                                <td className="text-[#1B7EB5] font-medium">{formatCurrency(record.cost)}</td>
                                                <td><StatusBadge status={record.status} /></td>
                                                {canManage() && (
                                                    <td>
                                                        <DropdownMenu>
                                                            <DropdownMenuTrigger asChild>
                                                                <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-500 hover:text-slate-700">
                                                                    <MoreVertical className="w-4 h-4" />
                                                                </Button>
                                                            </DropdownMenuTrigger>
                                                            <DropdownMenuContent align="end" className="bg-white border-slate-200">
                                                                <DropdownMenuItem 
                                                                    onClick={() => handleEdit(record)}
                                                                    className="cursor-pointer"
                                                                >
                                                                    <Edit className="w-4 h-4 mr-2" />
                                                                    {language === 'el' ? 'Επεξεργασία' : 'Edit'}
                                                                </DropdownMenuItem>
                                                                {isAdmin() && (
                                                                    <DropdownMenuItem 
                                                                        onClick={() => handleDelete(record.id)}
                                                                        className="cursor-pointer text-red-400 focus:text-red-400"
                                                                    >
                                                                        <Trash2 className="w-4 h-4 mr-2" />
                                                                        {language === 'el' ? 'Διαγραφή' : 'Delete'}
                                                                    </DropdownMenuItem>
                                                                )}
                                                            </DropdownMenuContent>
                                                        </DropdownMenu>
                                                    </td>
                                                )}
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            {/* Replacement Car Modal */}
            <Dialog open={showReplacementModal} onOpenChange={setShowReplacementModal}>
                <DialogContent className="bg-white border-slate-200 max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-slate-800 flex items-center gap-2">
                            <Car className="w-5 h-5 text-blue-500" />
                            {replacementStep === 1 ? 'Replacement Car Details' : 'Replacement Car Photos'}
                        </DialogTitle>
                    </DialogHeader>
                    
                    {replacementStep === 1 ? (
                        <div className="space-y-4 mt-4">
                            <p className="text-sm text-slate-600 mb-4">
                                Enter the temporary/rental replacement vehicle details:
                            </p>

                            {/* Vehicle Make & Model */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="form-group">
                                    <Label className="form-label">Make/Brand *</Label>
                                    <Input
                                        value={replacementCarData.make}
                                        onChange={(e) => setReplacementCarData({...replacementCarData, make: e.target.value})}
                                        className="bg-white border-slate-300 text-slate-900"
                                        placeholder="e.g., Toyota, Ford..."
                                        data-testid="replacement-make-input"
                                    />
                                </div>
                                <div className="form-group">
                                    <Label className="form-label">Model</Label>
                                    <Input
                                        value={replacementCarData.model}
                                        onChange={(e) => setReplacementCarData({...replacementCarData, model: e.target.value})}
                                        className="bg-white border-slate-300 text-slate-900"
                                        placeholder="e.g., Corolla, Focus..."
                                        data-testid="replacement-model-input"
                                    />
                                </div>
                            </div>

                            {/* License Plate */}
                            <div className="form-group">
                                <Label className="form-label">License Plate *</Label>
                                <Input
                                    value={replacementCarData.license_plate}
                                    onChange={(e) => setReplacementCarData({...replacementCarData, license_plate: e.target.value.toUpperCase()})}
                                    className="bg-white border-slate-300 text-slate-900 uppercase"
                                    placeholder="e.g., ABC-1234"
                                    data-testid="replacement-license-input"
                                />
                            </div>

                            {/* Odometer Reading */}
                            <div className="form-group">
                                <Label className="form-label">Current Kilometers *</Label>
                                <Input
                                    type="number"
                                    value={replacementCarData.odometer_reading}
                                    onChange={(e) => setReplacementCarData({...replacementCarData, odometer_reading: e.target.value})}
                                    className="bg-white border-slate-300 text-slate-900"
                                    placeholder="e.g., 45000"
                                    data-testid="replacement-km-input"
                                />
                            </div>

                            {/* Fuel Level Gauge */}
                            <div className="form-group">
                                <Label className="form-label">Fuel Level *</Label>
                                <div className="grid grid-cols-5 gap-2 mt-2">
                                    {fuelLevelOptions.map(option => (
                                        <button
                                            key={option.value}
                                            type="button"
                                            onClick={() => setReplacementCarData({...replacementCarData, fuel_level: option.value})}
                                            className={`p-3 rounded-lg border-2 transition-all ${
                                                replacementCarData.fuel_level === option.value
                                                    ? 'border-blue-500 bg-blue-50'
                                                    : 'border-slate-200 hover:border-slate-300'
                                            }`}
                                        >
                                            <div className={`text-lg font-mono ${option.color}`}>{option.icon}</div>
                                            <div className="text-xs text-slate-600 mt-1">{option.label}</div>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Notes */}
                            <div className="form-group">
                                <Label className="form-label">Notes</Label>
                                <Textarea
                                    value={replacementCarData.notes}
                                    onChange={(e) => setReplacementCarData({...replacementCarData, notes: e.target.value})}
                                    className="bg-white border-slate-300 text-slate-900"
                                    placeholder="Any additional notes about the replacement car..."
                                />
                            </div>

                            <div className="flex justify-end gap-3 pt-4">
                                <Button 
                                    variant="outline" 
                                    onClick={() => {
                                        setShowReplacementModal(false);
                                        setNeedsReplacementCar(false);
                                        setFormData(initialFormData);
                                        fetchData();
                                    }}
                                    className="border-slate-300"
                                >
                                    Skip
                                </Button>
                                <Button 
                                    onClick={handleReplacementCarSubmit}
                                    disabled={submitting || !replacementCarData.make || !replacementCarData.license_plate || !replacementCarData.odometer_reading}
                                    className="bg-blue-600 hover:bg-blue-500 text-white"
                                >
                                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Next: Upload Photos'}
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4 mt-4">
                            <p className="text-sm text-slate-600">
                                Upload 6 photos of the replacement car to document its condition.
                            </p>

                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                {photoTypes.map(photoType => (
                                    <div key={photoType} className="relative">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            className="hidden"
                                            ref={el => fileInputRefs.current[photoType] = el}
                                            onChange={(e) => handleReplacementPhotoUpload(photoType, e.target.files[0])}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => fileInputRefs.current[photoType]?.click()}
                                            disabled={uploadingPhoto === photoType || replacementPhotos[photoType]}
                                            className={`w-full aspect-square rounded-lg border-2 border-dashed transition-all flex flex-col items-center justify-center ${
                                                replacementPhotos[photoType]
                                                    ? 'border-green-500 bg-green-50'
                                                    : 'border-slate-300 hover:border-blue-400 hover:bg-blue-50'
                                            }`}
                                        >
                                            {uploadingPhoto === photoType ? (
                                                <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                                            ) : replacementPhotos[photoType] ? (
                                                <>
                                                    <Check className="w-8 h-8 text-green-500" />
                                                    <span className="text-xs text-green-600 mt-1">Uploaded</span>
                                                </>
                                            ) : (
                                                <>
                                                    <Camera className="w-8 h-8 text-slate-400" />
                                                    <span className="text-xs text-slate-500 mt-1">{photoLabels[photoType]}</span>
                                                </>
                                            )}
                                        </button>
                                    </div>
                                ))}
                            </div>

                            <div className="bg-slate-50 rounded-lg p-3 text-center">
                                <p className="text-sm text-slate-600">
                                    {Object.keys(replacementPhotos).length} of 6 photos uploaded
                                </p>
                            </div>

                            <div className="flex justify-end gap-3 pt-4">
                                <Button 
                                    variant="outline" 
                                    onClick={() => setReplacementStep(1)}
                                    className="border-slate-300"
                                >
                                    Back
                                </Button>
                                <Button 
                                    onClick={handleReplacementComplete}
                                    className="bg-green-600 hover:bg-green-500 text-white"
                                >
                                    <Check className="w-4 h-4 mr-2" />
                                    Complete Setup
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
